import { LinkIcon, CheckIcon, EyeOffIcon, SparklesIcon, MapPin, Home, Euro } from "lucide-react";
import { Badge } from "react-daisyui";
import { useTranslation } from "react-i18next";
import { useUserProfile } from "../../../hooks/useUserProfile";
import HardMatch from "../../../models/HardMatch";
import { getRealEstatePlatformMetadata } from "../../../models/RealEstatePlatform";
import { formatFirstSeen } from "./firstSeen";

export interface MatchCardProps {
    match: HardMatch;
    onClick: () => void;
}

const MatchCard: React.FC<MatchCardProps> = ({ match, onClick }) => {
    const { t, i18n } = useTranslation();
    // no-op
    const platform = getRealEstatePlatformMetadata(match.flat.site);
    const { subscriptionData } = useUserProfile();
    const firstSeen = formatFirstSeen(
        match.flat.created_at,
        i18n.resolvedLanguage ?? i18n.language,
    );
    
    return (
        <div 
            className="bg-white rounded-xl hover:shadow-md transition cursor-pointer overflow-hidden group"
            data-umami-event="Matching Card Click"
            data-testid="match-card"
            onClick={onClick}
        >
            <div className={`relative h-48 overflow-hidden ${(match.state === 'applied' || match.state === 'hidden') ? 'opacity-50' : ''}`}>
                {match.flat.image_urls.length > 0 ? (
                    <img 
                        src={match.flat.image_urls[0]} 
                        alt={match.flat.title} 
                        className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        {t('pages.matchingPage.matchCard.noImageAvailable')}
                    </div>
                )}

                {subscriptionData?.isSubscribed && (
                    <div className="absolute top-3 right-3 bg-white/95 px-2 py-1 rounded-md text-xs font-medium text-gray-700 flex items-center gap-1">
                        <LinkIcon size={14} /> {platform.title}
                    </div>
                )}

                {/* New/applied/hidden indicators */}
                {match.state === 'applied' && (
                    <div className='absolute top-3 left-3'>
                        <Badge color="success" className='badge-sm' data-testid="match-card-state-applied">
                            <CheckIcon size={14} className="mr-1" />
                            {t('pages.matchingPage.matchCard.applied')}
                        </Badge>
                    </div>
                )}
                {match.state === 'hidden' && (
                    <div className='absolute top-3 left-3'>
                        <Badge color="warning" className='badge-sm' data-testid="match-card-state-hidden">
                            <EyeOffIcon size={14} className="mr-1" />
                            {t('pages.matchingPage.matchCard.notInterested')}
                        </Badge>
                    </div>
                )}
                {!match.state && new Date().getTime() - new Date(match.flat.created_at).getTime() <= 2 * 24 * 60 * 60 * 1000 && (
                    <div className='absolute top-3 left-3'>
                        <Badge color="primary" className='badge-sm'>
                            <SparklesIcon size={12} className="mr-1" />
                            {t('pages.matchingPage.matchCard.new')}
                        </Badge>
                    </div>
                )}
            </div>

            <div className={`p-4 ${(match.state === 'applied' || match.state === 'hidden') ? 'opacity-50' : ''}`}>
                
                <h3 className="font-semibold text-lg mb-2 text-gray-900 line-clamp-1 break-words">{match.flat.title}</h3>

                {[match.flat.district, match.flat.city].some(Boolean) && (
                    <div className="flex items-center gap-1 text-gray-600 text-sm mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="line-clamp-1">{[match.flat.district, match.flat.city].filter(Boolean).join(', ')}</span>
                    </div>
                )}

                <div className="flex items-center mb-3 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                        <Home className="w-4 h-4" />
                        <span className={match.close_match_properties?.includes('rooms') ? 'text-red-700' : ''}>
                            {match.flat.rooms} {t('pages.matchingPage.matchCard.rooms')}
                        </span>
                    </div>
                    <span className="mx-2">•</span>
                    <div className="flex items-center gap-1">
                        {/*<Maximize2 className="w-4 h-4" />*/}
                        <span className={match.close_match_properties?.includes('size') ? 'text-red-700' : ''}>
                            {match.flat.size} m²
                        </span>
                    </div>
                </div>

                {firstSeen && (
                    <p className="text-sm text-gray-500 mb-3">
                        {t("pages.matchingPage.matchCard.firstSeen", { relativeTime: firstSeen })}
                    </p>
                )}

                {match.soft_match_checklist && match.soft_match_checklist.length > 0 && (
                    <ul className="text-sm mb-3 text-gray-700">
                        {match.soft_match_checklist.map((item, index) => (
                            <li key={index} className={`flex items-center`}>
                                {item.type === 'good' ? '✓' : '✗'} {item.reason}
                            </li>
                        ))}
                    </ul>
                )}

                <div className="pt-3 border-t flex items-center justify-between">
                    <div className="flex items-center gap-2 h-8">
                        <Euro className="w-5 h-5 text-blue-600" />
                        {match.flat.warm_rent || match.flat.cold_rent ? (
                            <div className="flex items-center gap-2">
                                <span className={`text-2xl font-bold text-gray-900 ${match.close_match_properties?.includes('rent') ? 'text-red-700' : ''}`}>
                                    {match.flat.warm_rent || match.flat.cold_rent}
                                </span>
                                <span className="text-sm text-gray-600">
                                    {match.flat.warm_rent ? t('pages.matchingPage.matchCard.warm') : t('pages.matchingPage.matchCard.cold')}
                                </span>
                            </div>
                        ) : (
                            <span className="text-sm text-gray-600">{t('pages.matchingPage.matchCard.priceOnRequest')}</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MatchCard;
